import { sprintf } from 'sprintf-js'

import lang from 'lang'
import { errorCodes, transactionTypes, errorTypes, JsonSchemaValidationErrorTypes } from '@/modules/base/util/constants'
import { toast } from '@/modules/base/components/toast/toastConfig'

const {
  REQUIRED,
  NO_NUMBER,
  NOT_EMPTY,
  NOT_ALLOWED,
  INVALID,
  TOO_LONG,
  NOT_LOCKED,
  EXISTS,
  NOT_CONFIRMED,
} = errorCodes

const {
  TRANSACTION_TYPE_CASH_INVOICE,
  TRANSACTION_TYPE_OFFER,
  TRANSACTION_TYPE_INVOICE,
  TRANSACTION_TYPE_DELIVERY_NOTE,
  TRANSACTION_TYPE_DEPOSIT_INVOICE,
  TRANSACTION_TYPE_RECURRING_INVOICE,
  TRANSACTION_TYPE_ORDER,
  TRANSACTION_TYPE_MANUAL_CREDIT_NOTE,
  TRANSACTION_TYPE_LETTER,
  TRANSACTION_TYPE_PURCHASE,
  TRANSACTION_TYPE_PARTIAL_INVOICE,
} = transactionTypes

export const getStringType = (transactionType) => {
  switch (transactionType) {
    case TRANSACTION_TYPE_CASH_INVOICE:
    case TRANSACTION_TYPE_INVOICE:
      return lang.transactionErrors.transactionTypeInvoice
    case TRANSACTION_TYPE_DEPOSIT_INVOICE:
      return lang.transactionErrors.transactionTypeDepositInvoice
    case TRANSACTION_TYPE_OFFER:
      return lang.transactionErrors.transactionTypeOffer
    case TRANSACTION_TYPE_DELIVERY_NOTE:
      return lang.transactionErrors.transactionTypeDeliveryNote
    case TRANSACTION_TYPE_ORDER:
      return lang.transactionErrors.transactionTypeOrder
    case TRANSACTION_TYPE_RECURRING_INVOICE:
      return lang.transactionErrors.transactionTypeRecurringInvoice
    case TRANSACTION_TYPE_MANUAL_CREDIT_NOTE:
      return lang.transactionErrors.transactionTypeManualCreditNote
    case TRANSACTION_TYPE_LETTER:
      return lang.transactionErrors.transactionTypeLetter
    case TRANSACTION_TYPE_PURCHASE:
      return lang.transactionErrors.transactionTypePurchase
    case TRANSACTION_TYPE_PARTIAL_INVOICE:
      return lang.transactionErrors.transactionTypePartialInvoice
  }
}

export const handleTransactionFormErrors = function(view, errors, transactionType) {
  let message = lang.defaultErrorMessage
  const stringType = getStringType(transactionType)

  for (const key in errors) {
    if (!errors.hasOwnProperty(key)) {
      continue
    }

    errors[key].map(({ code }) => {
      if (message !== lang.defaultErrorMessage) {
        return
      }

      switch (key) {
        case 'customerId':
          if (code === REQUIRED || code === NO_NUMBER) {
            message = sprintf(lang.transactionEmptyCustomerErrorMessage, stringType)
            view?.recipient?.openCustomerSelection() // TODO v2
          }
          break
        case 'positions':
          if (code === NOT_EMPTY || code === INVALID) {
            message = sprintf(lang.transactionEmptyPositionsErrorMessage, stringType)
            view?.positionNew?.openArticleSelection() // TODO v2
          }
          break
        case 'discountPercent':
          if (code === NOT_ALLOWED) {
            message = lang.transactionDiscountPercentErrorMessage
          }
          break
        case 'letterPaperSettingId':
          if (code === REQUIRED) {
            message = lang.transactionLetterPaperIdMissingErrorMessage
          }
          break
        case 'deliveryConditionId':
          if (code === REQUIRED) {
            message = sprintf(lang.transactionEmptyDeliveryConditionErrorMessage, stringType)
          }
          break
        case 'payConditionId':
          if (code === REQUIRED) {
            message = sprintf(lang.transactionEmptyPayConditionErrorMessage, stringType)
          }
          break
        case 'dunningRecipients':
          if (code === NOT_EMPTY) {
            message = lang.dunningRecipientEmptyRecipientsErrorMessage
          }
          break
        case 'depositInvoices':
          if (code === NOT_LOCKED) {
            message = lang.depositInvoice.errors.notAllDepositsLocked
          }
          break
        case 'closingInvoice':
          if (code === EXISTS) {
            message = lang.depositInvoice.errors.closingInvoiceExists
          }
          break
        case 'customerData.lastName':
          message = lang.transactionEmptyCustomerLastName
          break
        case 'customerData.companyName':
          message = lang.transactionEmptyCustomerCompanyName
          break
        case 'body.eInvoiceSettings':
          message = lang.transactionEInvoiceSettingsErrorMessage
          break
        case 'customer.email':
          message = lang.transactionEmptyCustomerEmail
          break
        case 'directDebitMandateId':
        case 'directDebitMandate':
          message = lang.transactionEmptyDirectDebitMandateId
          break
        case 'purposeOfUseText':
          if (code === INVALID) {
            message = lang.directDebitSetting.purposeOfUseTextInvalid
          }
          if (code === TOO_LONG) {
            message = lang.directDebitSetting.purposeError
          }
          break
      }
    })
  }

  toast.showToast({ type: 'error', message })
}

/**
 * DO-13065: the API refuses to send mail for an unverified sender address with
 * `403 { meta: { email: [{ code: 'NOT_CONFIRMED' }] } }`. Works on the object `request` rejects with
 * (`{ statusCode, body }`) as well as on a raw axios response (`{ status, data }`).
 */
export const isEmailNotConfirmedError = (error) => {
  const status = error?.statusCode ?? error?.status
  const body = error?.body ?? error?.data
  return status === 403 && body?.meta?.email?.[0]?.code === NOT_CONFIRMED
}

export const handleTransactionErrors = (error, context) => {
  const errorMeta = error?.body?.meta
  const errorName = error?.body?.name

  let message = lang.defaultErrorMessage
  if (errorName === errorTypes.JsonSchemaValidation) {
    if (errorMeta) {
      const errorParent = Object.keys(errorMeta)[0]
      const errorChild = errorMeta[errorParent][0]
      const errorKey = Object.keys(errorChild)[0]
      const errorValue = Object.values(errorChild)[0]
      switch (errorKey) {
        case JsonSchemaValidationErrorTypes.AdditionalProperty: {
          message = lang.errors.jsonSchemaValidationErrors.additionalProperty(errorValue)
          break
        }
        case JsonSchemaValidationErrorTypes.MissingProperty: {
          message = lang.errors.jsonSchemaValidationErrors.missingProperty(errorValue)
          break
        }
      }
    }
  } else if (errorName === errorTypes.FinanceApiError) {
    message = lang.errors.financeApiError.default
  }

  return toast.showToast({ message, type: 'error' })
}
